/** Public decoration only. No auth, product state, billing or client-side calendar. */
export const THEME_IDS = [
  'new_year', 'christmas', 'valentine', 'defender', 'womens_day', 'april_fools',
  'space_day', 'school_day', 'halloween', 'singles_day', 'black_friday',
] as const
export type ThemeId = typeof THEME_IDS[number]
export type Surface = 'landing' | 'miniapp' | 'admin'
export interface ThemeWindow { id: ThemeId; name: string; start_at: string | null; end_at: string }
export interface AppearanceStatus {
  theme: ThemeWindow | null
  source: 'auto' | 'manual' | 'off'
  server_time: string
  valid_until: string
  next_transition_at: string | null
  next: ThemeWindow | null
  manual_expired: boolean
}

export function parseAppearance(raw: unknown): AppearanceStatus | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as AppearanceStatus
  if (typeof p.server_time !== 'string' || typeof p.valid_until !== 'string') return null
  const start = Date.parse(p.server_time), end = Date.parse(p.valid_until)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 65_000) return null
  if (!['auto', 'manual', 'off'].includes(p.source)) return null
  if (p.theme !== null && (!p.theme || !THEME_IDS.includes(p.theme.id) || typeof p.theme.name !== 'string' || !Number.isFinite(Date.parse(p.theme.end_at)) || Date.parse(p.theme.end_at) <= start)) return null
  return p
}

export function leaseMilliseconds(payload: AppearanceStatus, roundTripMs: number): number {
  return Math.max(0, Date.parse(payload.valid_until) - Date.parse(payload.server_time) - roundTripMs)
}

/** Expiry uses monotonic time and the server clock; sleeping/offline tabs fail closed. */
export function installAppearance(surface: Surface): () => void {
  let stopped = false, pending = false, queued = false, deadline = 0
  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  let expireTimer: ReturnType<typeof setTimeout> | undefined
  let controller: AbortController | undefined
  const root = document.documentElement
  const clear = () => root.removeAttribute('data-season')
  const schedule = (ms: number) => {
    clearTimeout(refreshTimer)
    refreshTimer = setTimeout(() => void refresh(), ms)
  }
  const refresh = async () => {
    if (stopped) return
    if (pending) { queued = true; return }
    pending = true
    controller = new AbortController()
    const started = performance.now()
    const timeout = setTimeout(() => controller?.abort(), 4000)
    try {
      const response = await fetch(`/api/public/v1/appearance?surface=${surface}`, { cache: 'no-store', credentials: 'omit', signal: controller.signal })
      if (!response.ok) throw new Error('Appearance unavailable')
      const payload = parseAppearance(await response.json())
      if (!payload) throw new Error('Invalid appearance')
      if (stopped) return
      const remaining = leaseMilliseconds(payload, performance.now() - started)
      clearTimeout(expireTimer)
      if (remaining <= 0) { clear(); schedule(1000); return }
      deadline = performance.now() + remaining
      if (payload.theme) root.setAttribute('data-season', payload.theme.id)
      else clear()
      expireTimer = setTimeout(clear, remaining)
      schedule(Math.max(1000, remaining - 5000))
    } catch {
      if (!stopped) { if (performance.now() >= deadline) clear(); schedule(30_000) }
    } finally { clearTimeout(timeout); pending = false; if (queued && !stopped) { queued = false; void refresh() } }
  }
  const wake = () => {
    if (performance.now() >= deadline) clear()
    if (!document.hidden) void refresh()
  }
  document.addEventListener('visibilitychange', wake)
  window.addEventListener('aivy:appearance-refresh', wake)
  void refresh()
  return () => {
    stopped = true; controller?.abort(); clearTimeout(refreshTimer); clearTimeout(expireTimer)
    document.removeEventListener('visibilitychange', wake)
    window.removeEventListener('aivy:appearance-refresh', wake)
    clear()
  }
}
